import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/common/button";
import { CheckOption } from "../../../../components/common/check-option";
import { Text } from "../../../../components/common/typography";
import { useMe } from "../../../../swr";
import { useMePartner } from "../../../../swr";
import { Role } from "../../../../api";
import type { DocumentsQueryDto } from "../../../../api";
import { ChangeRequestDtoTypeEnum } from "../../../../api";

export interface ChangeRequestsFilterContentProps {
  filters: DocumentsQueryDto;
  onFiltersChange: (filters: DocumentsQueryDto) => void;
}

type TypeOption = { value: string; label: string };

const ADMIN_TYPE_OPTIONS: TypeOption[] = [
  { value: ChangeRequestDtoTypeEnum.BuyerProfileUpdate, label: "Buyer profile" },
  { value: ChangeRequestDtoTypeEnum.SellerProfileUpdate, label: "Seller profile" },
  { value: ChangeRequestDtoTypeEnum.ProductUpdate, label: "Product" },
  { value: ChangeRequestDtoTypeEnum.BankAccountCreate, label: "Bank account added" },
  { value: ChangeRequestDtoTypeEnum.BankAccountUpdate, label: "Bank account edited" },
  { value: ChangeRequestDtoTypeEnum.BankAccountDelete, label: "Bank account removed" },
];

const SELLER_TYPE_OPTIONS: TypeOption[] = [
  { value: ChangeRequestDtoTypeEnum.SellerProfileUpdate, label: "Profile" },
  { value: ChangeRequestDtoTypeEnum.ProductUpdate, label: "Product" },
  { value: ChangeRequestDtoTypeEnum.BankAccountCreate, label: "Bank account added" },
  { value: ChangeRequestDtoTypeEnum.BankAccountUpdate, label: "Bank account edited" },
  { value: ChangeRequestDtoTypeEnum.BankAccountDelete, label: "Bank account removed" },
];

const BUYER_TYPE_OPTIONS: TypeOption[] = [
  { value: ChangeRequestDtoTypeEnum.BuyerProfileUpdate, label: "Profile" },
];

export function ChangeRequestsFilterContent({
  filters,
  onFiltersChange,
}: ChangeRequestsFilterContentProps) {
  const [local, setLocal] = useState<DocumentsQueryDto>(filters);
  const { user } = useMe();
  const { isSeller } = useMePartner();

  useEffect(() => {
    setLocal(filters);
  }, [filters]);

  const typeOptions = useMemo<TypeOption[]>(() => {
    if (user?.role === Role.Admin) return ADMIN_TYPE_OPTIONS;
    if (isSeller) return SELLER_TYPE_OPTIONS;
    return BUYER_TYPE_OPTIONS;
  }, [user?.role, isSeller]);

  // The generated DocumentsQueryDto doesn't carry a `type` field (OpenAPI
  // generator collapses several queries to the same name; see CLAUDE.md
  // "API call rules" rule 6). The runtime BE controller does accept it.
  const currentType = (local as { type?: string }).type;

  const handleTypeChange = (value: string) => {
    setLocal((prev) => {
      const next = { ...(prev as object) } as Record<string, unknown>;
      if (value === currentType) delete next.type;
      else next.type = value;
      return next as DocumentsQueryDto;
    });
  };

  const handleApply = () => onFiltersChange(local);
  const handleClear = () => {
    const cleared: DocumentsQueryDto = {};
    setLocal(cleared);
    onFiltersChange(cleared);
  };

  const activeCount = currentType ? 1 : 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6">
        <div className="flex flex-col">
          <Text variant="bodySm" tone="muted" className="pb-2">
            Refine your search
          </Text>

          <div className="flex flex-col gap-3 border-t border-borderDefault py-5">
            <Text variant="capsSm" tone="accent" inline>
              REQUEST TYPE
            </Text>
            <div className="flex flex-col gap-1.5">
              {typeOptions.map((opt) => (
                <CheckOption
                  key={opt.value}
                  label={opt.label}
                  checked={currentType === opt.value}
                  onChange={() => handleTypeChange(opt.value)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-borderDefault bg-bgWhite px-6 py-6">
        <div className="flex gap-3">
          <div className="flex-1">
            <Button variant="primary" fullWidth onClick={handleApply}>
              Apply filters{activeCount > 0 ? ` (${activeCount})` : ""}
            </Button>
          </div>
          <div className="flex-1">
            <Button variant="secondary" fullWidth onClick={handleClear}>
              Clear all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
